import React, { useEffect, useState } from "react";
import FsLightbox from "fslightbox-react";
import Auth from "../../utils/auth";
import { FaDownload, FaShareAlt, FaEye, FaTrashAlt } from "react-icons/fa";
import { API_URL } from "../../api/APIUrl";

const GalleryItem = ({ data }) => {
  const [toggler, setToggler] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tempUrl, setTempUrl] = useState(null);

  useEffect(() => {
    // Check administrative flags securely
    if (Auth.isAdmin() !== "false" && Auth.isAdmin() !== false) {
      setIsAdmin(true);
    }
  }, []);

  const viewPDF = async (id) => {
    const rawUrl = await generateMaterialUrl(id);
    if (rawUrl) {
      const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
        rawUrl
      )}&embedded=true`;
      setTempUrl(viewerUrl);
      setToggler(!toggler);
    }
  };

  const generateMaterialUrl = async (id) => {
    const res = await fetch(`${API_URL}/api/materials/${id}/download`);
    const data = await res.json();
    return data.url;
  };

  const deletePost = async (id) => {
    await fetch(`${API_URL}/api/materials/${id}`, {
      method: "DELETE",
    });
    window.location.reload();
  };

  const confirmDelete = async (id) => {
    const confirm = window.confirm(
      "Are you sure you want to delete this material? This action cannot be undone."
    );
    if (confirm) {
      await deletePost(id);
    }
  };

  const handleDownload = async (id) => {
    const url = await generateMaterialUrl(id);
    window.open(url, "_blank");
  };

  const handleShare = async (id, title = "Check this out") => {
    const url = await generateMaterialUrl(id);
    if (!url) {
      alert("No download URL received.");
      return;
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title,
          text: "Please click on the link to download the file.",
          url,
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("Share failed:", err);
      }
    } else {
      const subject = encodeURIComponent(`File Share: ${title}`);
      const body = encodeURIComponent(
        `Please click on the link to download the file: ${title}\n\nLink to download:\n${url}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;

      try {
        await navigator.clipboard.writeText(url);
        alert("Opening Email App... (Link also copied to clipboard!)");
      } catch (err) {
        console.error("Clipboard failed:", err);
      }
    }
  };

  // --- COMPONENT INLINE STYLES ---
  const imageWrapperStyle = {
    position: "relative",
    width: "100%",
    paddingTop: "133%", // Clean 3:4 Aspect Ratio layout for document architecture
    backgroundColor: "#f1f5f9",
    overflow: "hidden",
  };

  const thumbnailStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const bodyStyle = {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "space-between",
  };

  const actionButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  return (
    <>
      <div
        className="h-100 d-flex flex-column"
        style={{ width: "100%" }}
        onMouseEnter={(e) => {
          const img = e.currentTarget.querySelector(".catalog-thumb");
          if (img) img.style.transform = "scale(1.04)";
        }}
        onMouseLeave={(e) => {
          const img = e.currentTarget.querySelector(".catalog-thumb");
          if (img) img.style.transform = "scale(1.0)";
        }}
      >
        {/* Document Frame Canvas */}
        <div style={imageWrapperStyle}>
          <img
            className="catalog-thumb"
            src={data.thumbnailUrl}
            alt={data.postTitle}
            style={thumbnailStyle}
          />

          {/* Contextual Floating Admin Controls */}
          {isAdmin && (
            <button
              onClick={() => confirmDelete(data._id)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 5,
                backgroundColor: "rgba(239, 68, 68, 0.9)",
                color: "#ffffff",
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                cursor: "pointer",
              }}
              title="Delete Material Entry"
            >
              <FaTrashAlt size={14} />
            </button>
          )}
        </div>

        {/* Dynamic Typography Meta Container */}
        <div style={bodyStyle}>
          <div className="mb-3">
            {data.category && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#3b82f6",
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                {data.category}
              </span>
            )}
            <h5
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
                lineHeight: "1.4",
                textTransform: "capitalize",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {data.postTitle}
            </h5>
          </div>

          {/* Action Control Button Strip */}
          <div className="d-flex gap-2 mt-auto">
            <button
              style={actionButtonStyle}
              onClick={() => handleDownload(data._id)}
              title="Download Document File"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#007bff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
              }}
            >
              <FaDownload />
            </button>
            <button
              style={actionButtonStyle}
              onClick={() => handleShare(data._id, data.postTitle)}
              title="Share File Link"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#007bff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
              }}
            >
              <FaShareAlt />
            </button>
            <button
              style={{
                ...actionButtonStyle,
                backgroundColor: "#007bff",
                color: "#ffffff",
                border: "1px solid #007bff",
              }}
              onClick={() => viewPDF(data._id)}
              title="Quick Preview Viewer"
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#0056b3")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#007bff")
              }
            >
              <FaEye className="me-2" /> View
            </button>
          </div>
        </div>
      </div>

      {/* Embedded Lightbox Gateway */}
      <FsLightbox
        toggler={toggler}
        sources={[
          <iframe
            src={tempUrl}
            title={data.postTitle}
            width="100%"
            height="600px"
            style={{ border: "none", borderRadius: "12px" }}
            key={tempUrl}
          />,
        ]}
      />
    </>
  );
};

export default GalleryItem;
